/**
 * Shared Operate incident data — backend APIs only (AMS + engineering + memory).
 */
import { useMemo } from 'react';
import { useDashboard } from '../../../hooks/useDashboard';
import {
  useEnvironmentPromotions,
  useMissionDecision,
  useProjectAmsIncident,
  useProjectAmsIncidents,
  useSkillExecutions,
} from '../../../services/api';
import {
  useEngineeringSession,
  useSessionChangeset,
  useSessionValidation,
} from '../../../services/engineering';
import {
  useEngineeringMemoryDecision,
  useEngineeringPatterns,
  useObservationStatus,
  usePatternMatch,
  useReuseValidation,
} from '../../../services/engineeringMemory';
import { summarizeEngineeringArtifacts } from '../../../lib/engineeringArtifacts';

export function useOperateIncidentData(projectId: string, incidentId: string | null) {
  const listQ = useProjectAmsIncidents(projectId);
  const incidentQ = useProjectAmsIncident(projectId, incidentId);
  const dashQ = useDashboard(projectId, incidentId);
  const decisionQ = useMissionDecision(projectId, incidentId);
  const skillsQ = useSkillExecutions(projectId);
  const promotionsQ = useEnvironmentPromotions(projectId);

  const sessionId = useMemo(() => {
    const inc = incidentQ.data;
    return (
      inc?.linked_session_id ||
      inc?.mission?.assigned_engineering_mission?.session_id ||
      decisionQ.data?.linked_session_id ||
      undefined
    );
  }, [incidentQ.data, decisionQ.data]);

  const sessionQ = useEngineeringSession(projectId, sessionId);
  const changesetQ = useSessionChangeset(projectId, sessionId);
  const validationQ = useSessionValidation(projectId, sessionId);

  // Existing /api/v2/engineering/patterns — memory-first surface
  const patternsQ = useEngineeringPatterns(projectId);
  const matchQ = usePatternMatch(incidentId);
  const reuseQ = useReuseValidation(incidentId);
  const memoryDecisionQ = useEngineeringMemoryDecision(incidentId);
  const observationQ = useObservationStatus(incidentId);

  const rcaArtifacts = useMemo(
    () => summarizeEngineeringArtifacts(incidentQ.data),
    [incidentQ.data],
  );

  return {
    projectId,
    incidentId,
    sessionId,
    incidents: listQ.data ?? [],
    incidentsLoading: listQ.isLoading,
    incidentsError: listQ.error,
    incident: incidentQ.data,
    incidentLoading: incidentQ.isLoading,
    incidentError: incidentQ.error,
    dashboard: dashQ.data?.dashboard,
    dashboardLoading: dashQ.isLoading,
    decision: decisionQ.data,
    decisionLoading: decisionQ.isLoading,
    session: sessionQ.data,
    sessionLoading: sessionQ.isLoading,
    changeset: changesetQ.data,
    changesetLoading: changesetQ.isLoading,
    sessionValidation: validationQ.data,
    sessionValidationLoading: validationQ.isLoading,
    promotions: promotionsQ.data ?? [],
    promotionsLoading: promotionsQ.isLoading,
    skillExecutions: skillsQ.data ?? [],
    skillsLoading: skillsQ.isLoading,
    // Engineering memory
    patterns: patternsQ.data?.patterns ?? [],
    patternsLoading: patternsQ.isLoading,
    patternMatch: matchQ.data,
    patternMatchLoading: matchQ.isLoading,
    reuseReports: reuseQ.data?.reports ?? [],
    reuseLoading: reuseQ.isLoading,
    memoryDecision: memoryDecisionQ.data,
    memoryDecisionLoading: memoryDecisionQ.isLoading,
    observation: observationQ.data,
    observationLoading: observationQ.isLoading,
    rcaArtifacts,
  };
}

export type OperateIncidentData = ReturnType<typeof useOperateIncidentData>;
